"use server";

import { auth } from "@/auth";
import type { ActionResult } from "@/types/server-action";
import {
  buildBodyworkDraftUpdate,
  saveDraftBodyworkDataSchema,
  type SaveDraftBodyworkDataInput,
} from "@/lib/schemas/cat/vehicle-bodywork-save-draft";
import { validateParams } from "@/lib/utils";
import { prisma } from "@/prisma";
import { updateDraftAfterStepSave } from "@/lib/cat/draft-step-save";
import { buildDraftLockHolder } from "@/lib/cat/draft-lock";
import type { Draft } from "@/prisma/generated/client";

async function assertDraftExists(draftId: number): Promise<void> {
  const draft = await prisma.draft.findUnique({
    where: { id: draftId },
    select: { id: true },
  });

  if (!draft) {
    throw new Error("Brouillon introuvable.");
  }
}

async function assertActiveBodyTypeExists(bodyTypeId: number): Promise<void> {
  const bodyType = await prisma.bodyType.findFirst({
    where: {
      id: bodyTypeId,
      isActive: true,
      deletedAt: null,
    },
    select: { id: true },
  });

  if (!bodyType) {
    throw new Error("Le type de carrosserie sélectionné est invalide.");
  }
}

export async function saveDraftBodyworkData(
  params: SaveDraftBodyworkDataInput,
): Promise<ActionResult<Draft>> {
  try {
    const session = await auth();
    if (!session?.user?.id) {
      return { success: false, message: "Non authentifié." };
    }
    const payload = validateParams(saveDraftBodyworkDataSchema, params);
    await assertDraftExists(payload.id);

    if (payload.needsBodywork) {
      await assertActiveBodyTypeExists(payload.bodyTypeId);
    }

    const sessionKvps =
      session.data?.kvps?.current?.identifier ?? String(payload.id);

    const lockHolder = buildDraftLockHolder(session.user);

    const savedDraft = await updateDraftAfterStepSave({
      draftId: payload.id,
      stepId: "vehicle_bodywork",
      userId: lockHolder.userId,
      userName: lockHolder.userName,
      kvps: sessionKvps,
      data: buildBodyworkDraftUpdate(payload),
    });

    return { success: true, data: savedDraft };
  } catch (error) {
    console.error("[saveDraftBodyworkData]", error);

    if (error instanceof Error && error.message.length > 0) {
      return { success: false, message: error.message };
    }

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}
